import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from '../axiosConfig';
import CustomerFacingSubtotal from '../components/checkout/CustomerFacingSubtotal';

const ReceiptPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const receiptRef = useRef(null);

  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      if (!token) {
        setError('Invalid receipt link. Missing authentication token.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/invoices/${orderId}/receipt?token=${token}`);
        if (response.data.success) {
          setReceipt(response.data.receipt);
        } else {
          setError(response.data.error || 'Failed to load receipt');
        }
      } catch (err) {
        console.error('Receipt fetch error:', err);
        setError(err.response?.data?.error || 'Failed to load receipt. Please check the link and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [orderId, token]);

  const formatCurrency = (amount) => {
    const currencyCode = receipt?.currency || 'NGN';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Adire-inspired SVG pattern for border
  const adirePattern = `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231e3a5f' fill-opacity='0.15'%3E%3Cpath d='M20 0L0 20h8l12-12 12 12h8L20 0zm0 40L0 20h8l12 12 12-12h8L20 40z'/%3E%3Ccircle cx='20' cy='20' r='4'/%3E%3Ccircle cx='0' cy='0' r='2'/%3E%3Ccircle cx='40' cy='0' r='2'/%3E%3Ccircle cx='0' cy='40' r='2'/%3E%3Ccircle cx='40' cy='40' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#1e3a5f',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading receipt...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          padding: '32px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            background: '#fef2f2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg width="24" height="24" fill="none" stroke="#ef4444" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4m0 4h.01" />
            </svg>
          </div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>
            Unable to Load Receipt
          </h2>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '20px'
    }}>
      {/* Print Styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .receipt-container {
            box-shadow: none !important;
            margin: 0 !important;
            max-width: 100% !important;
          }
        }
      `}</style>

      {/* Receipt Card */}
      <div
        ref={receiptRef}
        className="receipt-container"
        style={{
          maxWidth: '600px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          border: '8px solid transparent',
          borderImage: adirePattern,
          borderImageSlice: 40,
          borderImageWidth: '8px',
          position: 'relative'
        }}
      >
        {/* Adire pattern top strip */}
        <div style={{
          height: '12px',
          background: adirePattern,
          backgroundColor: '#1e3a5f'
        }} />

        {/* Header */}
        <div style={{
          padding: '24px',
          textAlign: 'center',
          borderBottom: '1px solid #e5e7eb'
        }}>
          {receipt.store?.logo && (
            <img
              src={receipt.store.logo}
              alt={receipt.store.name}
              style={{
                maxWidth: '100px',
                maxHeight: '60px',
                objectFit: 'contain',
                marginBottom: '12px'
              }}
            />
          )}
          <h1 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 4px', color: '#1e3a5f' }}>
            {receipt.store?.name || 'Receipt'}
          </h1>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
            Receipt #{receipt.receiptNumber}
          </p>
        </div>

        {/* Status Badge */}
        <div style={{
          padding: '16px 24px',
          background: '#dcfce7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <svg width="20" height="20" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M5 13l4 4L19 7" />
          </svg>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>
            Payment Successful
          </span>
        </div>

        {/* Receipt Details */}
        <div style={{ padding: '24px' }}>
          {/* Transaction Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginBottom: '24px',
            fontSize: '13px'
          }}>
            <div>
              <p style={{ color: '#6b7280', margin: '0 0 4px' }}>Order Date</p>
              <p style={{ color: '#111827', fontWeight: '500', margin: 0 }}>
                {formatDate(receipt.orderDate)}
              </p>
            </div>
            <div>
              <p style={{ color: '#6b7280', margin: '0 0 4px' }}>Payment Date</p>
              <p style={{ color: '#111827', fontWeight: '500', margin: 0 }}>
                {formatDate(receipt.paidAt)}
              </p>
            </div>
            <div>
              <p style={{ color: '#6b7280', margin: '0 0 4px' }}>Payment Method</p>
              <p style={{ color: '#111827', fontWeight: '500', margin: 0 }}>
                {receipt.paymentMethod || 'Online Payment'}
              </p>
            </div>
            <div>
              <p style={{ color: '#6b7280', margin: '0 0 4px' }}>Reference</p>
              <p style={{ color: '#111827', fontWeight: '500', margin: 0, wordBreak: 'break-all' }}>
                {receipt.paymentReference || '-'}
              </p>
            </div>
          </div>

          {/* Customer Info */}
          <div style={{
            background: '#f9fafb',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', margin: '0 0 8px', textTransform: 'uppercase' }}>
              Customer
            </h3>
            <p style={{ margin: 0, fontWeight: '500', color: '#111827', fontSize: '14px' }}>
              {receipt.customer?.name || 'Customer'}
            </p>
            {receipt.customer?.email && (
              <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '13px' }}>
                {receipt.customer.email}
              </p>
            )}
            {receipt.customer?.phone && (
              <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '13px' }}>
                {receipt.customer.phone}
              </p>
            )}
          </div>

          {/* Line Items */}
          <div style={{
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', margin: '0 0 12px', textTransform: 'uppercase' }}>
              Items Purchased
            </h3>
            {receipt.items?.map((item, index) => (
              <div
                key={item.id || index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < receipt.items.length - 1 ? '1px solid #e5e7eb' : 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        width: '40px',
                        height: '40px',
                        objectFit: 'cover',
                        borderRadius: '6px'
                      }}
                    />
                  )}
                  <div>
                    <p style={{ margin: 0, fontWeight: '500', color: '#111827', fontSize: '14px' }}>
                      {item.name}
                    </p>
                    <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: '12px' }}>
                      {formatCurrency(item.price)} x {item.quantity}
                    </p>
                  </div>
                </div>
                <p style={{ margin: 0, fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                  {formatCurrency(item.total)}
                </p>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{
            background: '#f9fafb',
            borderRadius: '8px',
            padding: '16px'
          }}>
            <CustomerFacingSubtotal
              subtotal={receipt.subtotal}
              processingFee={
                receipt.serviceCharge > 0
                  && String(receipt.serviceChargeWhoPays || 'customer').toLowerCase() === 'customer'
                  ? receipt.serviceCharge
                  : 0
              }
              formatCurrency={formatCurrency}
              compact
              rowStyle={{ marginBottom: '8px', fontSize: '14px' }}
              labelStyle={{ color: '#6b7280', fontWeight: 400 }}
              valueStyle={{ color: '#111827', fontWeight: 400 }}
              subLabel={false}
            />
            {receipt.taxTotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Tax</span>
                <span style={{ color: '#111827' }}>{formatCurrency(receipt.taxTotal)}</span>
              </div>
            )}
            {(receipt.shippingFee > 0 || receipt.deliveryFee > 0) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Delivery</span>
                <span style={{ color: '#111827' }}>{formatCurrency(receipt.shippingFee || receipt.deliveryFee)}</span>
              </div>
            )}
            {receipt.discountTotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span style={{ color: '#6b7280' }}>Discount</span>
                <span style={{ color: '#22c55e' }}>-{formatCurrency(receipt.discountTotal)}</span>
              </div>
            )}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: '12px',
              borderTop: '2px solid #1e3a5f',
              marginTop: '8px'
            }}>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e3a5f' }}>Total Paid</span>
              <span style={{ fontSize: '16px', fontWeight: '700', color: '#1e3a5f' }}>{formatCurrency(receipt.total)}</span>
            </div>
          </div>

          {/* Notes */}
          {receipt.notes && (
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: '#fffbeb',
              borderRadius: '8px',
              borderLeft: '4px solid #f59e0b'
            }}>
              <h4 style={{ fontSize: '12px', fontWeight: '600', color: '#92400e', margin: '0 0 8px', textTransform: 'uppercase' }}>
                Notes
              </h4>
              <p style={{ fontSize: '13px', color: '#78350f', margin: 0, whiteSpace: 'pre-wrap' }}>
                {receipt.notes}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 24px',
          borderTop: '1px solid #e5e7eb',
          background: '#f9fafb',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '15px', fontWeight: '600', color: '#1e3a5f', margin: '0 0 4px' }}>
            Thank you for your purchase!
          </p>
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 12px' }}>
            Please keep this receipt for your records.
          </p>
          <div style={{ fontSize: '12px', color: '#9ca3af' }}>
            <p style={{ margin: '4px 0' }}>
              {receipt.store?.name}
            </p>
            {receipt.store?.phone && (
              <p style={{ margin: '4px 0' }}>{receipt.store.phone}</p>
            )}
            {receipt.store?.email && (
              <p style={{ margin: '4px 0' }}>{receipt.store.email}</p>
            )}
            {(receipt.store?.address || receipt.store?.city) && (
              <p style={{ margin: '4px 0' }}>
                {[receipt.store.address, receipt.store.city, receipt.store.state, receipt.store.country]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
          </div>
        </div>

        {/* Adire pattern bottom strip */}
        <div style={{
          height: '12px',
          background: adirePattern,
          backgroundColor: '#1e3a5f'
        }} />
      </div>

      {/* Print Button - Below the receipt */}
      <div className="no-print" style={{
        maxWidth: '600px',
        margin: '20px auto 0',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <button
          onClick={handlePrint}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#1e3a5f',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z" />
          </svg>
          Print Receipt
        </button>
      </div>
    </div>
  );
};

export default ReceiptPage;
